<template>
  <div>
    <div class="wrapper" v-if="!files.length">
      <input type="file" multiple class="input-file" @change="filesChange" />
      <div class="text-center">
        <icon name="upload" class="upload-icon" />
        <span> <b>Add file</b> or drop your files here </span>
        <br />
        <span> ({{ buildFileTypes() }} up to {{ mapFileSize() }}) </span>
      </div>
    </div>
    <div v-else>
      <div class="wrapper-file add-more">
        <icon name="upload-small" class="upload-icon-small" />
        <span>Add more files</span>
        <input type="file" multiple class="input-file" @change="filesChange" />
      </div>
      <div
        class="wrapper-file"
        v-for="(file, index) in files"
        v-bind:key="index"
      >
        <div class="body-file">
          <div class="body-file-text">
            <icon :name="determineIcon(file.type)" />
            <span style="margin-left: 8px">{{ file.name }}</span>
          </div>
          <div @click="deleteFile(index)" class="cursor-pointer delete-icon">
            <icon name="x-circle" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseType from "./BaseType.vue";
import { QuestionType } from "../../models/QuestionModel";
import icon from "../../assets/svg/Icon.vue";

export default {
  extends: BaseType,
  name: QuestionType.FileUpload,
  components: {
    icon
  },
  data() {
    return {
      files: [],
      errors: {
        fileType: false,
        fileSize: false,
        fileLimit: false
      }
    };
  },
  methods: {
    onEnter() {
      this.dirty = true;

      this._onEnter();
    },

    isValid() {
      return true;
    },

    filesChange(e) {
      const files = e.target.files;
      for (let i = 0; i <= files.length - 1; i++) {
        if (this.files.length + files.length > ~~this.question.maxFile) {
          this.$toast.error(`Maximum file upload is ${this.question.maxFile}.`);
          return;
        }

        if (~~files[i].size > ~~this.question.maxSize) {
          this.$toast.error(
            `File size should be less than ${this.mapFileSize()}.`
          );
          return;
        }

        if (!this.isFileTypeAllowed(files[i].type)) {
          this.$toast.error("File type is not allowed.");
          return;
        }

        this.files.push(files[i]);
      }
    },

    determineIcon(type) {
      switch (type) {
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          return "word";

        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          return "excel";

        case "application/pdf":
          return "pdf";

        case "image/jpg":
          return "image";

        case "image/jpeg":
          return "image";

        case "image/png":
          return "image";

        case "image/gif":
          return "image";

        case "video/mp4":
          return "video";

        case "video/mov":
          return "video";

        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
          return "ppt";

        default:
          return "file";
      }
    },

    isFileTypeAllowed(type) {
      if (this.question.fileTypes.other) {
        return true;
      }

      let allowedTypes = [];

      if (this.question.fileTypes.image) {
        allowedTypes = [
          ...allowedTypes,
          "image/jpg",
          "image/jpeg",
          "image/png",
          "image/gif"
        ];
      }

      if (this.question.fileTypes.document) {
        allowedTypes = [
          ...allowedTypes,
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        ];
      }

      if (this.question.fileTypes.video) {
        allowedTypes = [...allowedTypes, "video/mp4", "video/quicktime"];
      }

      return allowedTypes.includes(type);
    },

    buildFileTypes() {
      let types = [];

      if (this.question.fileTypes.other) {
        types.push("file");
        return types.toString();
      }

      if (this.question.fileTypes.image) {
        types = [...types, "jpg", "jpeg", "png", "gif"];
      }

      if (this.question.fileTypes.document) {
        types = [...types, "pdf", "xls", "doc", "ppt"];
      }

      if (this.question.fileTypes.video) {
        types = [...types, "mp4", "mov"];
      }

      return types.toString().replace(/,/g, ", ");
    },

    mapFileSize() {
      switch (this.question.maxSize) {
        case 512000:
          return "500 KB";
        case 1048576:
          return "1 MB";
        case 5242880:
          return "5 MB";
        case 10485760:
          return "10 MB";
        default:
          return "500 KB";
      }
    },

    deleteFile(index) {
      this.files.splice(index, 1);
    }
  },
  watch: {
    files: {
      immediate: true,
      deep: true,
      handler() {
        Object.assign(this.question, {
          files: this.files,
          answered: this.files.length > 0 ? true : false
        });
      }
    }
  }
};
</script>

<style scoped>
.wrapper {
  width: 100%;
  height: 180px;
  background: var(--vff-opinion-scale);
  border: 1px dashed var(--vff-answer-color);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 24px;
  cursor: pointer;
  position: relative;
  margin: auto;
}

.upload-icon {
  margin-bottom: 16px;
}

.upload-icon svg,
.upload-icon-small svg {
  fill: var(--vff-answer-color);
}

.upload-icon-small {
  margin-right: 8px;
}

.delete-icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.input-file {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  cursor: pointer;
}

.wrapper-file {
  width: 100%;
  height: 44px;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--vff-opinion-scale);
  position: relative;
}

.body-file {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.body-file-text {
  display: flex;
  justify-content: center;
  align-items: center;
}

.add-more {
  border: 1px dashed var(--vff-answer-color);
  background: var(--vff-opinion-scale);
}

.file-upload-text {
  font-size: 14px;
  width: 230px;
  height: 20px;
  overflow: hidden;
  margin: 0px 8px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
